import {
    ChecklistItem,
    ChecklistItemOrigin,
    ChecklistItemStatus,
    DocumentOrigin,
    DocumentType,
    Signer
} from 'generated/credit-analysis-graphql';
import { getKeyByValue } from 'utils/utils';

function getPendingType(items: ChecklistItem[]) {
    return items
        .filter(({ status }) => status === ChecklistItemStatus.Pending)
        .map(({ allowedDocuments }) => allowedDocuments);
}

function getPartnerWithPendingDocuments(signer: Signer[]) {
    return signer
        .find(({ checklist: { items } }) =>
            items.some(({ status, origin }) => status === ChecklistItemStatus.Pending
                && origin === ChecklistItemOrigin.UploadForm));
}

const checklistLabel = {
    [DocumentType.CertificateOfIncorporation]: {
        label: 'Contrato social',
        type: 'SocialContact'
    },
    [DocumentType.MicroBusinessesCertificate]: {
        label: 'Contrato social',
        type: 'SocialContact'
    },
    [DocumentType.CompanyBankStatement]: {
        label: 'Extratos Bancários',
        type: 'BankStatement'
    },
    [DocumentType.PartnerIdentity]: {
        label: 'Identidade',
        type: 'PartnerIdentity'
    },
    [DocumentType.PartnerDriverLicence]: {
        label: 'Identidade',
        type: 'PartnerIdentity'
    },
    [DocumentType.PartnerSelfie]: {
        label: 'Selfie',
        type: 'PartnerIdentity'
    },
    [DocumentType.PartnerDriverLicenceVerse]: {
        label: 'CNH - Verso',
        type: 'PartnerIdentity'
    },
    [DocumentType.CompanyCnpjRegularity]: {
        label: 'Regularidade do CNPJ',
        type: 'CompanyDocument'
    },
    [DocumentType.CompanyMateriality]: {
        label: 'Materialidade da Empresa',
        type: 'CompanyDocument'
    },
    [DocumentType.CompanyProofOfAddress]: {
        label: 'Comprovante de Endereço da Empresa',
        type: 'CompanyDocument'
    },
    [DocumentType.ConfirmationAudio]: {
        label: 'Áudio de Confirmação',
        type: 'Audio'
    },
    [DocumentType.DeathCertificate]: {
        label: 'Certidão de Óbito',
        type: 'LegalDocument'
    },
    [DocumentType.DownPaymentReceipt]: {
        label: 'Comprovante de Entrada',
        type: 'PaymentReceipt'
    },
    [DocumentType.FgtsRegularityCertificate]: {
        label: 'Certidão de Regularidade do FGTS',
        type: 'CompanyDocument'
    },
    [DocumentType.IndividualEntrepreneurCertificate]: {
        label: 'Certificado de Microempreendedor Individual',
        type: 'SocialContact'
    },
    [DocumentType.Invoice]: {
        label: 'Nota Fiscal',
        type: 'Invoice'
    },
    [DocumentType.LaborMinistryRegularityCertificate]: {
        label: 'Certidão de Regularidade do Ministério do Trabalho',
        type: 'CompanyDocument'
    },
    [DocumentType.PartnerBankStatement]: {
        label: 'Extrato Bancário do Sócio',
        type: 'BankStatement'
    },
    [DocumentType.PartnerCpf]: {
        label: 'CPF',
        type: 'PartnerIdentity'
    },
    [DocumentType.PartnerCpfRegularity]: {
        label: 'Regularidade do CPF',
        type: 'PartnerIdentity'
    },
    [DocumentType.PartnerIncomeStatement]: {
        label: 'Declaração de Renda',
        type: 'IncomeStatement'
    },
    [DocumentType.PartnerIncomeTaxReturn]: {
        label: 'Declaração de Imposto de Renda',
        type: 'IncomeTax'
    },
    [DocumentType.PartnerMarriageCertificate]: {
        label: 'Certidão de Casamento',
        type: 'LegalDocument'
    },
    [DocumentType.PartnerProofOfAddress]: {
        label: 'Comprovante de Endereço',
        type: 'PartnerIdentity'
    },
    [DocumentType.PartnerProofOfIncome]: {
        label: 'Comprovante de Renda',
        type: 'IncomeStatement'
    },
    [DocumentType.PgfnRegularityCertificate]: {
        label: 'Certidão de Regularidade da PGFN',
        type: 'CompanyDocument'
    },
    [DocumentType.Unknown]: {
        label: 'Documento Desconhecido',
        type: 'Unknown'
    },
    [DocumentType.PartnerIdentityVerse]: {
        label: 'Verso do documento',
        type: 'Unknown'
    }
}

const documentName = {
    [DocumentType.CertificateOfIncorporation]: "Contrato Social",
    [DocumentType.MicroBusinessesCertificate]: "Certificado MEI",
    [DocumentType.CompanyBankStatement]: "Extrato Bancário",
    [DocumentType.PartnerIdentity]: "RG",
    [DocumentType.PartnerDriverLicence]: "CNH",
}

const getChecklistVariables = (
    loanApplicationId: string, selectedType: DocumentType, signerId?) => ({
        variables: {
            loanApplicationId,
            documentType: selectedType,
            ...(signerId && { signerId })
        }
    })

const getSignedUrlObject = (
    filename: string,
    contentType: string,
    loanApplicationId: string,
    documentType: string,
    signerId?,
) => {
    const selectedType: DocumentType = DocumentType[getKeyByValue(DocumentType, documentType)]
    const filenameFormatted = filename.includes('.jfif') ? filename.replace('jfif', 'jpg') : filename

    return {
        variables: {
            loanApplicationId,
            documentType: selectedType,
            documentOrigin: DocumentOrigin.UploadedByUser,
            contentType,
            filename: filenameFormatted,
            insertUserEmail: '',
            ...(signerId && { signerId })
        }
    }
}

const GetAllowedDocuments = (documents: DocumentType[]) =>
    documents.map(docs => ({ label: documentName[docs], value: docs }))

export {
    checklistLabel,
    getPendingType,
    getPartnerWithPendingDocuments,
    documentName,
    getChecklistVariables,
    getSignedUrlObject, GetAllowedDocuments
};
